import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Checkbox,
  CircularProgress,
  InputAdornment,
  Tooltip,
  TablePagination,
} from "@mui/material";
import { Close, Search } from "@mui/icons-material";
import { useState, useEffect, useMemo, useCallback, useRef, memo } from "react";
import { FilterColumn } from "components/StripedDataGrid/FilterColumn";
import { debounce } from "lodash";
import { TIME_DEBOUNCE } from "hooks/usePagination";
import {
  importarTipologiasDoMOF,
  IImportarTipologiasMOFRequest,
} from "services/modules/tarefaDetalhe";
import { useNavigation } from "contexts/notificationsContext";
import { api, irParaLogin } from "services/axios/api";

// ============================================
// RESOLUÇÃO DE IMAGEM DA TIPOLOGIA (N7.1)
// ============================================
// Base para assets estáticos quando o backend retorna caminhos relativos.
// Espelha o buildImageSrc de Tipologias/detalhe.tsx (data:, absolutos e
// caminhos relativos via /api/Pacote/imagem?path=...).
const API_ASSETS_BASE_URL =
  (window as any).configs?.REACT_APP_BASE_URL?.replace("/api/", "") ||
  "http://localhost:3099";

const buildImageSrc = (url?: string | null): string => {
  if (!url) return "";
  const trimmed = url.trim();
  if (trimmed.startsWith("data:")) return trimmed;
  if (trimmed.startsWith("http://") || trimmed.startsWith("https://")) return trimmed;

  // Já contém query string (ex.: /api/Pacote/imagem?path=...) → usar como está
  if (trimmed.includes("?path=") || trimmed.includes("/api/Pacote/imagem")) {
    return trimmed.startsWith("/")
      ? `${API_ASSETS_BASE_URL}${trimmed}`
      : `${API_ASSETS_BASE_URL}/${trimmed}`;
  }

  // Apenas um nome de arquivo (sem caminho) → resolver via /api/Pacote/imagem
  if (!trimmed.includes("/")) {
    const path = `assets/figuras_perfil/${trimmed}`;
    return `${API_ASSETS_BASE_URL}/api/Pacote/imagem?path=${encodeURIComponent(path)}`;
  }

  // Caminhos relativos
  return trimmed.startsWith("/")
    ? `${API_ASSETS_BASE_URL}${trimmed}`
    : `${API_ASSETS_BASE_URL}/${trimmed}`;
};

// ============================================
// INTERFACES
// ============================================

export interface ITipologiaMOF {
  idEsquadria: number;
  codigo: string;
  descricao: string;
  idLinha: number;
  nomeLinha: string;
  idReferencia: number;
  nomeReferencia: string;
  idStatus: number;
  // imagem da tipologia (N7.1): substitui o logo da linha
  imagemUrl?: string | null;
  projetistas: string[];
}

interface ModalImportarTipologiasMOFProps {
  isOpen: boolean;
  onClose: () => void;
  idTarefaCaso: number;
  onTipologiasImportadas: () => void;
  // 24/08 (pedido do PO): em Caso NOVO nada grava até o SALVAR — quando informado,
  // a confirmação devolve a seleção (com os dados de exibição) em vez de chamar a API.
  onSelecionarSemSalvar?: (tipologias: ITipologiaMOF[]) => void;
}

// Interface para resposta paginada da API
interface IApiResponsePaginada {
  items: any[];
  page: number;
  limit: number;
  totalRecords: number;
  totalPages: number;
}

// ============================================
// COMPONENTE AUXILIAR - IMAGEM TIPOLOGIA (com lazy loading)
// ============================================

interface ImagemTipologiaProps {
  codigo: string;
  imagemUrl?: string | null;
}

// Componente memoizado para exibir a IMAGEM DA TIPOLOGIA (N7.1).
// A imagem vem do campo `imagemUrl` (preenchido pelo backend) e é resolvida
// por buildImageSrc. Se vier vazia ou falhar o carregamento, mostra placeholder.
const ImagemTipologia = memo(({ codigo, imagemUrl }: ImagemTipologiaProps) => {
  const [imageError, setImageError] = useState(false);
  const src = useMemo(() => buildImageSrc(imagemUrl), [imagemUrl]);

  useEffect(() => {
    setImageError(false);
  }, [src]);

  // Placeholder: sem imagem ou erro de carregamento
  if (!src || imageError) {
    return (
      <Box
        sx={{
          width: 40,
          height: 40,
          backgroundColor: "grey.200",
          borderRadius: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: "0.7rem",
          color: "text.secondary",
        }}
        title={`Imagem não disponível para ${codigo}`}
      >
        {codigo.substring(0, 2).toUpperCase()}
      </Box>
    );
  }

  // Imagem com tooltip (zoom ao passar o mouse)
  return (
    <Tooltip
      title={
        <Box
          component="img"
          src={src}
          alt={`Tipologia ${codigo}`}
          sx={{
            width: "230px",
            maxHeight: "230px",
            objectFit: "contain",
            display: "block",
            backgroundColor: "#fff",
            borderRadius: 1,
          }}
        />
      }
      arrow
      placement="right"
      componentsProps={{
        tooltip: {
          sx: {
            backgroundColor: "transparent",
            padding: 0,
            boxShadow: "0 4px 20px rgba(0,0,0,0.15)",
            maxWidth: "none",
          },
        },
        arrow: {
          sx: {
            color: "#fff",
          },
        },
      }}
    >
      <Box
        component="img"
        src={src}
        alt={`Tipologia ${codigo}`}
        loading="lazy"
        onError={() => setImageError(true)}
        sx={{
          width: 40,
          height: 40,
          objectFit: "contain",
          cursor: "pointer",
          backgroundColor: "transparent",
          borderRadius: 1,
          padding: 0.5,
          border: "1px solid",
          borderColor: "grey.300",
        }}
        title={codigo}
      />
    </Tooltip>
  );
});

ImagemTipologia.displayName = "ImagemTipologia";

// ============================================
// COMPONENTE DE LINHA DA TABELA (memoizado)
// ============================================

interface TipologiaRowProps {
  tipologia: ITipologiaMOF;
  isSelected: boolean;
  onToggle: (id: number) => void;
}

const TipologiaRow = memo(({ tipologia, isSelected, onToggle }: TipologiaRowProps) => {
  return (
    <TableRow
      hover
      sx={{
        cursor: "pointer",
        backgroundColor: isSelected ? "action.selected" : "inherit",
      }}
      onClick={() => onToggle(tipologia.idEsquadria)}
    >
      <TableCell padding="checkbox">
        <Checkbox
          checked={isSelected}
          onChange={() => onToggle(tipologia.idEsquadria)}
          onClick={(e) => e.stopPropagation()}
        />
      </TableCell>
      <TableCell>
        <ImagemTipologia codigo={tipologia.codigo} imagemUrl={tipologia.imagemUrl} />
      </TableCell>
      <TableCell>
        <Typography variant="body2" fontWeight={500}>
          {tipologia.codigo}
        </Typography>
      </TableCell>
      <TableCell>
        <Typography variant="body2">{tipologia.descricao}</Typography>
      </TableCell>
      <TableCell>
        <Typography variant="body2">{tipologia.nomeLinha}</Typography>
      </TableCell>
      <TableCell>
        <Typography variant="body2" sx={{ wordBreak: "break-word" }}>
          {tipologia.projetistas && tipologia.projetistas.length > 0
            ? tipologia.projetistas.join(", ")
            : "-"}
        </Typography>
      </TableCell>
    </TableRow>
  );
});

TipologiaRow.displayName = "TipologiaRow";

// ============================================
// COMPONENTE PRINCIPAL
// ============================================

export const ModalImportarTipologiasMOF = ({
  isOpen,
  onClose,
  idTarefaCaso,
  onTipologiasImportadas,
  onSelecionarSemSalvar,
}: ModalImportarTipologiasMOFProps) => {
  const { openNotification } = useNavigation();
  // Snapshot dos itens já exibidos (todas as páginas carregadas) — permite montar a
  // lista pendente com os dados de exibição dos selecionados no modo sem salvar.
  const itensPorIdRef = useRef(new Map<number, ITipologiaMOF>());
  const [loading, setLoading] = useState(false);
  const [loadingTipologias, setLoadingTipologias] = useState(false);
  const [tipologias, setTipologias] = useState<ITipologiaMOF[]>([]);
  const [tipologiasSelecionadas, setTipologiasSelecionadas] = useState<number[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [searchTermDebounced, setSearchTermDebounced] = useState("");
  
  // Estado de paginação
  const [page, setPage] = useState(0); // MUI usa 0-based
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [totalRecords, setTotalRecords] = useState(0);

  // Refs para filtros das colunas
  const refCodigo = useRef<HTMLInputElement>(null);
  const refDescricao = useRef<HTMLInputElement>(null);
  const refLinha = useRef<HTMLInputElement>(null);
  const refProjetista = useRef<HTMLInputElement>(null);

  // Filtros por coluna
  const [filtros, setFiltros] = useState<{
    codigo?: string;
    descricao?: string;
    linha?: string;
    projetista?: string;
  }>({});

  const [filtrosDebounced, setFiltrosDebounced] = useState<{
    codigo?: string;
    descricao?: string;
    linha?: string;
    projetista?: string;
  }>({});

  // Debounce para busca geral
  const debouncedSearch = useCallback(
    debounce((value: string) => {
      setSearchTermDebounced(value);
      setPage(0); // Voltar para primeira página ao buscar
    }, TIME_DEBOUNCE),
    []
  );

  // Debounce para filtros de coluna
  const debouncedFiltros = useCallback(
    debounce((novosFiltros: typeof filtros) => {
      setFiltrosDebounced(novosFiltros);
      setPage(0); // Voltar para primeira página ao filtrar
    }, TIME_DEBOUNCE),
    []
  );

  // Atualizar filtros debounced quando filtros mudarem
  useEffect(() => {
    debouncedFiltros(filtros);
  }, [filtros, debouncedFiltros]);

  // Atualizar busca debounced quando searchTerm mudar
  useEffect(() => {
    debouncedSearch(searchTerm);
  }, [searchTerm, debouncedSearch]);

  // Carregar tipologias do MOF (com paginação server-side)
  const carregarTipologias = useCallback(async () => {
    setLoadingTipologias(true);
    try {
      // Construir parâmetros de query
      const params: Record<string, any> = {
        page: page + 1, // API usa 1-based
        limit: rowsPerPage,
      };

      // Busca geral
      if (searchTermDebounced.trim()) {
        params.search = searchTermDebounced.trim();
      }

      // Filtros de coluna
      if (filtrosDebounced.codigo) {
        params.codigo = filtrosDebounced.codigo;
      }
      if (filtrosDebounced.descricao) {
        params.descricao = filtrosDebounced.descricao;
      }
      if (filtrosDebounced.linha) {
        params.linha = filtrosDebounced.linha;
      }

      // Buscar esquadrias (tipologias) do MOF com paginação
      const { data } = await api.get<IApiResponsePaginada>("/TarefaDetalhe/casos/esquadrias", {
        params,
      });

      // Mapear dados para formato ITipologiaMOF
      const tipologiasMapeadas: ITipologiaMOF[] = (data.items || []).map((item: any) => {
        const projetistasRaw = item.projetistas ?? item.Projetistas;
        const projetistasArray = Array.isArray(projetistasRaw) ? projetistasRaw : [];

        return {
          idEsquadria: item.idEsquadria || item.IdEsquadria || 0,
          codigo: item.codigo || item.Codigo || "",
          descricao: item.descricao || item.Descricao || "",
          idLinha: item.idLinha || item.IdLinha || 0,
          nomeLinha: item.nomeLinha || item.NomeLinha || "-",
          idReferencia: item.idReferencia || item.IdReferencia || 0,
          nomeReferencia: item.nomeReferencia || item.NomeReferencia || "-",
          idStatus: item.idStatus || item.IdStatus || 0,
          imagemUrl: item.imagemUrl || item.ImagemUrl || null,
          projetistas: projetistasArray,
        };
      });

      tipologiasMapeadas.forEach((t) => itensPorIdRef.current.set(t.idEsquadria, t));
      setTipologias(tipologiasMapeadas);
      setTotalRecords(data.totalRecords || 0);
    } catch (error: any) {
      console.error("Erro ao carregar tipologias:", error);

      const status = error?.status || error?.response?.status;
      let errorMessage = "Erro ao carregar tipologias do MOF";

      if (status === 401) {
        const token = sessionStorage.getItem("@MOF:token");
        if (!token) {
          errorMessage = "Sessão expirada. Redirecionando para login...";
          openNotification({
            isOpen: true,
            message: errorMessage,
            type: "warning",
          });
          setTimeout(() => {
            irParaLogin();
          }, 2000);
        } else {
          errorMessage = "Você não tem permissão para acessar tipologias.";
        }
      } else if (status === 403) {
        errorMessage = "Acesso negado. Você não tem permissão para acessar tipologias.";
      } else if (status && status >= 500) {
        errorMessage = "Erro interno do servidor ao carregar tipologias.";
      } else if (error?.message) {
        errorMessage = error.message;
      } else if (error?.response?.data?.message) {
        errorMessage = error.response.data.message;
      }

      openNotification({
        isOpen: true,
        message: errorMessage,
        type: status === 401 && !sessionStorage.getItem("@MOF:token") ? "warning" : "error",
      });
      setTipologias([]);
      setTotalRecords(0);
    } finally {
      setLoadingTipologias(false);
    }
  }, [page, rowsPerPage, searchTermDebounced, filtrosDebounced, openNotification]);

  // Recarregar quando parâmetros mudarem
  useEffect(() => {
    if (isOpen) {
      carregarTipologias();
    }
  }, [isOpen, carregarTipologias]);

  // Reset ao abrir o modal
  useEffect(() => {
    if (isOpen) {
      setTipologiasSelecionadas([]);
      setSearchTerm("");
      setSearchTermDebounced("");
      setFiltros({});
      setFiltrosDebounced({});
      setPage(0);
      // Limpar campos de filtro
      if (refCodigo.current) refCodigo.current.value = "";
      if (refDescricao.current) refDescricao.current.value = "";
      if (refLinha.current) refLinha.current.value = "";
      if (refProjetista.current) refProjetista.current.value = "";
    }
  }, [isOpen]);

  // Filtrar localmente apenas por projetista (não suportado pelo backend)
  const tipologiasFiltradas = useMemo(() => {
    if (!filtrosDebounced.projetista) {
      return tipologias;
    }
    const filtro = filtrosDebounced.projetista.toLowerCase();
    return tipologias.filter((t) =>
      t.projetistas.some((p) => p.toLowerCase().includes(filtro))
    );
  }, [tipologias, filtrosDebounced.projetista]);

  // Reteste 21/08 (item 7): saída de emergência do estado vazio — zera busca geral,
  // filtros de coluna e paginação de uma vez.
  const handleLimparFiltros = useCallback(() => {
    setSearchTerm("");
    setSearchTermDebounced("");
    setFiltros({});
    setFiltrosDebounced({});
    setPage(0);
  }, []);

  const handleToggleSelecao = useCallback((idEsquadria: number) => {
    setTipologiasSelecionadas((prev) =>
      prev.includes(idEsquadria)
        ? prev.filter((id) => id !== idEsquadria)
        : [...prev, idEsquadria]
    );
  }, []);

  const handleSelecionarTodas = useCallback(() => {
    if (tipologiasSelecionadas.length === tipologiasFiltradas.length && tipologiasFiltradas.length > 0) {
      setTipologiasSelecionadas([]);
    } else {
      setTipologiasSelecionadas(tipologiasFiltradas.map((t) => t.idEsquadria));
    }
  }, [tipologiasSelecionadas.length, tipologiasFiltradas]);

  const handleChangePage = useCallback((_: unknown, newPage: number) => {
    setPage(newPage);
  }, []);

  const handleChangeRowsPerPage = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  }, []);

  const handleImportar = async () => {
    if (tipologiasSelecionadas.length === 0) {
      openNotification({
        isOpen: true,
        message: "Selecione pelo menos uma tipologia para importar",
        type: "warning",
      });
      return;
    }

    // 24/08: Caso novo — devolve a seleção para a tela; a gravação ocorre no SALVAR.
    if (onSelecionarSemSalvar) {
      const selecionadas = tipologiasSelecionadas
        .map((id) => itensPorIdRef.current.get(id))
        .filter((t): t is ITipologiaMOF => !!t);
      onSelecionarSemSalvar(selecionadas);
      openNotification({
        isOpen: true,
        message: `${selecionadas.length} tipologia(s) adicionada(s) — serão gravadas ao salvar o Caso.`,
        type: "info",
      });
      onClose();
      return;
    }

    setLoading(true);
    try {
      const request: IImportarTipologiasMOFRequest = {
        idTarefaCaso,
        idsTipologias: tipologiasSelecionadas,
      };

      await importarTipologiasDoMOF(idTarefaCaso, request);

      openNotification({
        isOpen: true,
        message: `${tipologiasSelecionadas.length} tipologia(s) importada(s) com sucesso!`,
        type: "success",
      });

      onTipologiasImportadas();
      onClose();
    } catch (error: any) {
      openNotification({
        isOpen: true,
        message: error.message || "Erro ao importar tipologias",
        type: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      maxWidth="xl"
      fullWidth
      PaperProps={{
        sx: { minHeight: 500, maxWidth: "95vw" },
      }}
    >
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">Cadastro de tipologias do MOF</Typography>
          <IconButton onClick={onClose} size="small">
            <Close />
          </IconButton>
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Selecione as tipologias que farão parte do caso
        </Typography>
      </DialogTitle>

      <DialogContent sx={{ overflowX: "auto" }}>
        {/* Busca */}
        <TextField
          fullWidth
          placeholder="Buscar por código, descrição, linha ou projetista..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          size="small"
          sx={{ mb: 2 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search fontSize="small" />
              </InputAdornment>
            ),
          }}
        />

        {/* Tabela de Tipologias — o cabeçalho com os filtros fica SEMPRE montado
            (reteste 21/08, item 7): antes, loading/vazio desmontavam os inputs e o
            filtro digitado ficava ativo no estado, invisível e impossível de editar,
            zerando toda busca seguinte (AND com o termo novo). */}
        <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 500, overflowX: "auto" }}>
          <Table size="small" sx={{ tableLayout: "auto", minWidth: 1200 }} stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox" sx={{ width: 50, minWidth: 50, backgroundColor: "background.paper" }}>
                  <Checkbox
                    indeterminate={
                      tipologiasSelecionadas.length > 0 &&
                      tipologiasSelecionadas.length < tipologiasFiltradas.length
                    }
                    checked={
                      tipologiasFiltradas.length > 0 &&
                      tipologiasSelecionadas.length === tipologiasFiltradas.length
                    }
                    onChange={handleSelecionarTodas}
                  />
                </TableCell>
                <TableCell sx={{ width: 90, minWidth: 90, backgroundColor: "background.paper" }}>
                  <Typography variant="body2" fontWeight={600} noWrap>
                    Imagem
                  </Typography>
                </TableCell>
                <TableCell sx={{ width: 150, minWidth: 150, backgroundColor: "background.paper" }}>
                  <FilterColumn
                    inputRef={refCodigo}
                    label="Código"
                    name="codigo"
                    value={filtros.codigo ?? ""}
                    onChange={(event) => {
                      setFiltros((prev) => ({ ...prev, codigo: event.target.value }));
                    }}
                  />
                </TableCell>
                <TableCell sx={{ width: 280, minWidth: 280, backgroundColor: "background.paper" }}>
                  <FilterColumn
                    inputRef={refDescricao}
                    label="Descrição"
                    name="descricao"
                    value={filtros.descricao ?? ""}
                    onChange={(event) => {
                      setFiltros((prev) => ({ ...prev, descricao: event.target.value }));
                    }}
                  />
                </TableCell>
                <TableCell sx={{ width: 150, minWidth: 150, backgroundColor: "background.paper" }}>
                  <FilterColumn
                    inputRef={refLinha}
                    label="Linha"
                    name="linha"
                    value={filtros.linha ?? ""}
                    onChange={(event) => {
                      setFiltros((prev) => ({ ...prev, linha: event.target.value }));
                    }}
                  />
                </TableCell>
                <TableCell sx={{ width: 200, minWidth: 200, backgroundColor: "background.paper" }}>
                  <FilterColumn
                    inputRef={refProjetista}
                    label="Projetista"
                    name="projetista"
                    value={filtros.projetista ?? ""}
                    onChange={(event) => {
                      setFiltros((prev) => ({ ...prev, projetista: event.target.value }));
                    }}
                  />
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loadingTipologias ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 6, border: 0 }}>
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : tipologiasFiltradas.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 4, border: 0, color: "text.secondary" }}>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      {searchTerm || Object.values(filtros).some(v => v)
                        ? "Nenhuma tipologia encontrada"
                        : "Nenhuma tipologia disponível"}
                    </Typography>
                    {(searchTerm || Object.values(filtros).some(v => v)) && (
                      <Button size="small" variant="outlined" onClick={handleLimparFiltros}>
                        LIMPAR FILTROS
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ) : (
                tipologiasFiltradas.map((tipologia) => (
                  <TipologiaRow
                    key={tipologia.idEsquadria}
                    tipologia={tipologia}
                    isSelected={tipologiasSelecionadas.includes(tipologia.idEsquadria)}
                    onToggle={handleToggleSelecao}
                  />
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Paginação */}
        <TablePagination
          rowsPerPageOptions={[25, 50, 100]}
          component="div"
          count={totalRecords}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage="Linhas por página:"
          labelDisplayedRows={({ from, to, count }) =>
            `${from}–${to} de ${count !== -1 ? count : `mais de ${to}`}`
          }
        />

        {tipologiasSelecionadas.length > 0 && (
          <Box sx={{ mt: 2, p: 1, backgroundColor: "action.selected", borderRadius: 1 }}>
            <Typography variant="body2">
              {tipologiasSelecionadas.length} tipologia(s) selecionada(s)
            </Typography>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        <Button
          onClick={onClose}
          variant="outlined"
          color="inherit"
          sx={{
            textTransform: "uppercase",
            fontWeight: 600,
            minWidth: 120,
          }}
        >
          CANCELAR
        </Button>
        <Button
          onClick={handleImportar}
          variant="contained"
          color="primary"
          disabled={loading || tipologiasSelecionadas.length === 0}
          sx={{
            textTransform: "uppercase",
            fontWeight: 600,
            minWidth: 120,
          }}
        >
          {loading ? <CircularProgress size={20} /> : "IMPORTAR"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
